import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import type { ApiResponse } from './types';
import router from '@/router';
import { keysToCamelCase } from '@literack/shared';

let isRedirectingToLogin = false;

const instance: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let getTokenFn: (() => Promise<string | null>) | null = null;

export const setTokenGetter = (fn: () => Promise<string | null>) => {
  getTokenFn = fn;
};

instance.interceptors.request.use(
  async (config) => {
    if (getTokenFn) {
      const token = await getTokenFn();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    return response;
  },
  (error) => {
    if (error.response) {
      const { status } = error.response;

      if (status === 401 && !isRedirectingToLogin) {
        isRedirectingToLogin = true;
        router.push({ name: 'Login', query: { redirect: router.currentRoute.value.fullPath } }).finally(() => {
          isRedirectingToLogin = false;
        });
      }

      if (status === 403) {
        router.push('/forbidden');
      }
    }

    return Promise.reject(error);
  }
);

async function request<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await instance.request(config);
  const data = response.data;
  
  if (data && typeof data === 'object') {
    if ('data' in data) {
      const transformedData = keysToCamelCase(data.data);
      if (Array.isArray(transformedData) && 'meta' in data) {
        return {
          items: transformedData,
          total: data.meta?.total || 0,
          page: Math.floor((data.meta?.offset || 0) / (data.meta?.limit || 50)) + 1,
          pageSize: data.meta?.limit || 50,
          totalPages: Math.ceil((data.meta?.total || 0) / (data.meta?.limit || 50)),
        } as T;
      }
      return transformedData as T;
    }
  }
  
  return keysToCamelCase(data) as T;
}

export const api = {
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return request<T>({ ...config, method: 'GET', url });
  },

  post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return request<T>({ ...config, method: 'POST', url, data });
  },

  put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return request<T>({ ...config, method: 'PUT', url, data });
  },

  patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return request<T>({ ...config, method: 'PATCH', url, data });
  },

  delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return request<T>({ ...config, method: 'DELETE', url });
  },
};

export default api;
